import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectClient } from '@client/model/selectors';
import { AppState } from '@client/model/store';
import {
  buildComparisonScenarios,
  calcAdjustedSelfFunding,
  calcFundingBreakdown,
  calcProtectedAssets,
  calcSelfFundingCapacity,
  calcTotalPolicyCost,
} from '@client/util/ltcFinancialPlanning';
import { createQuoteWithROIandProjectedCoverage } from '@client/util/policyQuoteCalculations';

import { useLtcPlanningCalculations } from './useLtcPlanningCalculations';

export function useLtcPlanComparison() {
  const client = useSelector(selectClient);
  const {
    yearsTillLtc,
    projectedLtcCost,
    projectedLtcCostWithoutInflation,
    contributionMonthCount,
    yearsTillLtcRounded,
  } = useLtcPlanningCalculations();

  const { selectedAssets, priorityAssets, accountDetails, chosenPolicy, fundingAllocations } =
    useSelector(
      (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
        state.ltcFinancialPlanning,
    );

  // Calculate policy coverage from the chosen policy
  const policyCoverage = useMemo(() => {
    if (!chosenPolicy || !client) {
      return chosenPolicy?.ltcBenefitTotal || 350000;
    }

    try {
      const quoteWithROI = createQuoteWithROIandProjectedCoverage(chosenPolicy, client);
      return quoteWithROI.projectedCostCoverage || chosenPolicy.ltcBenefitTotal || 350000;
    } catch (error) {
      // If calculation fails, fall back to base benefit
      console.warn('Failed to calculate projected cost coverage:', error);
      return chosenPolicy.ltcBenefitTotal || 350000;
    }
  }, [chosenPolicy, client]);

  // Self-funding capacity without insurance deductions
  const selfFundingCapacity = useMemo(
    () => calcSelfFundingCapacity(selectedAssets, accountDetails, yearsTillLtc, contributionMonthCount),
    [selectedAssets, accountDetails, yearsTillLtc, contributionMonthCount],
  );

  // Adjusted self-funding with insurance premium deductions
  const adjustedSelfFunding = useMemo(
    () =>
      calcAdjustedSelfFunding(
        selectedAssets,
        accountDetails,
        fundingAllocations,
        yearsTillLtc,
        contributionMonthCount,
      ),
    [selectedAssets, accountDetails, fundingAllocations, yearsTillLtc, contributionMonthCount],
  );

  // Total cost paid for the policy
  const totalPolicyCost = useMemo(
    () => calcTotalPolicyCost(fundingAllocations),
    [fundingAllocations],
  );

  // Build comparison scenarios
  const comparison = useMemo(
    () =>
      buildComparisonScenarios(
        selfFundingCapacity,
        adjustedSelfFunding,
        policyCoverage,
        projectedLtcCost,
      ),
    [selfFundingCapacity, adjustedSelfFunding, policyCoverage, projectedLtcCost],
  );

  // Funding breakdown with ROI
  const fundingBreakdown = useMemo(
    () => calcFundingBreakdown(fundingAllocations, accountDetails, yearsTillLtc),
    [fundingAllocations, accountDetails, yearsTillLtc],
  );

  // Protected assets with future values
  const protectedAssets = useMemo(
    () => calcProtectedAssets(priorityAssets, accountDetails, yearsTillLtc, contributionMonthCount),
    [priorityAssets, accountDetails, yearsTillLtc, contributionMonthCount],
  );

  // Net difference between scenarios
  const netDifference = comparison.withInsurance.surplus - comparison.withoutInsurance.surplus;

  // Coverage gaps and surpluses
  const coverageGap = comparison.withoutInsurance.gap;
  const coverageSurplus = comparison.withoutInsurance.surplus;
  const isWellPositioned = selfFundingCapacity >= projectedLtcCost;

  return {
    // Base values from useLtcPlanningCalculations
    yearsTillLtc,
    yearsTillLtcRounded,
    projectedLtcCost,
    projectedLtcCostWithoutInflation,
    contributionMonthCount,

    // State references
    selectedAssets,
    priorityAssets,
    accountDetails,
    chosenPolicy,
    fundingAllocations,
    client,

    // Calculated values
    policyCoverage,
    selfFundingCapacity,
    adjustedSelfFunding,
    totalPolicyCost,
    comparison,
    fundingBreakdown,
    protectedAssets,
    netDifference,

    // Coverage assessment values
    coverageGap,
    coverageSurplus,
    isWellPositioned,
  };
}

